import {
  Tracker,
  BaseEvent,
  BaseTrackingArgs,
  TrackingConfig,
} from '@chargeafter/tracker';
import { Context, getContext } from './contextManager';
import { FlipOrigin } from '../constants';
import logger from './loggerManager';
import { replaceHyphenWithSpace } from '../formattingUtils';
import { environment } from '../environment';

class SalesAppTracker<T> {
  private tracker = new Tracker<T>();

  init(config?: TrackingConfig) {
    if (config) {
      this.tracker.init({ ...config, sendPageView: false });
      this.tracker.setBaseTrackingArgs({
        channel: 'in_store',
      });
    }
  }

  setBaseTrackingArgs(args: BaseTrackingArgs) {
    this.tracker.setBaseTrackingArgs(args);
  }

  reset() {
    this.tracker.reset();
  }

  page(path: string, args: T & Omit<BaseEvent, 'eventName'>) {
    const context = getContext() || {};

    logger.log(
      `Page view - ${replaceHyphenWithSpace(context.view)}${
        context.availableCredit?.creditAmount
          ? `, Cart amount: ${context.availableCredit?.creditAmount}`
          : ''
      }${
        context.origin
          ? `, flip origin: ${replaceHyphenWithSpace(context.origin)}`
          : ''
      }`,
      {
        function: context.view || 'page',
        filename: context.view || 'trackerManager.ts',
        ...context,
      },
    );

    this.tracker.page(path, args);
  }
  event(args: T & BaseEvent) {
    this.tracker.event(args);
  }
  identify(id: string) {
    this.setBaseTrackingArgs({ user_id: id });
    this.tracker.identify(id);
  }
}

interface SalesAppEvents {
  application_id?: string;
  lender_id?: string;
  available_credit_amount?: number;
  available_minimum_amount?: number;
  confirmation_token?: string;
  cart?: string;
  consumer_id?: string;
  merchant_id?: string;
  merchant_order_id?: string;
  merchant_signed_application_id?: string;
  store_id?: string;
  page_name?: string;
  user_id?: string;
  peer_type?: string;
  impersonated_enabled?: string;
  event_source?: string;
  button_name?: string;
  button_type?: ButtonType;
  app_name?: string;
  fs_link?: string;
  origin?: FlipOrigin;
}

type ButtonType = 'click';

export const trackerNormalize = ({
  applicationId,
  availableCredit,
  confirmationToken,
  consumer,
  merchantId,
  merchantOrderId,
  signedApplicationId,
  storeId,
  userProfile,
  view,
  fullStoryLink,
  origin,
}: Context): SalesAppEvents => {
  return {
    application_id: applicationId,
    lender_id: availableCredit?.lender.id,
    available_credit_amount: availableCredit?.creditAmount,
    available_minimum_amount: availableCredit?.minimumAmount,
    confirmation_token: confirmationToken,
    consumer_id: consumer?.id,
    merchant_id: merchantId,
    merchant_order_id: merchantOrderId,
    merchant_signed_application_id: signedApplicationId,
    store_id: storeId,
    page_name: view,
    user_id: userProfile?.userId,
    peer_type: userProfile?.peerType,
    impersonated_enabled: userProfile?.impersonateEnabled,
    event_source: 'salesApp',
    app_name: 'salesApp',
    fs_link: fullStoryLink,
    origin: origin,
  };
};

export const tracker = new SalesAppTracker<SalesAppEvents>();

tracker.init({
  service: 'ga',
  appName: 'salesApp',
  isProduction: environment.envName === 'production',
  gaToken: environment.googleAnalyticsKey,
  trackingKey: environment.mixpanelKey,
});

export const sendButtonClickEvent = ({
  buttonName,
}: {
  buttonName: string;
}) => {
  const context = getContext() || {};
  logger.log(
    `Button click,  page: ${replaceHyphenWithSpace(
      context.view,
    )}, buttonName: ${replaceHyphenWithSpace(buttonName)}${
      context.availableCredit?.creditAmount
        ? `, Cart amount: ${context.availableCredit?.creditAmount}`
        : ''
    } ${context.origin ? `, flip origin: ${context.origin}` : ''}`,
    {
      function: buttonName,
      filename: context.view || 'trackerManager.ts',
      ...context,
    },
  );
  tracker.event({
    eventName: 'button_click',
    ...trackerNormalize(context),
    button_type: 'click',
    button_name: buttonName,
  });
};
